import React, { useState, useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { isRowAveragesUid } from '@aytm/app/insights/dashboard/lib/config'
import { isBenchmark } from '@aytm/app/insights/dashboard/helpers/checks'

import { Dropdown } from 'primereact/dropdown'

import { sortOptions } from '@aytm/app/insights/dashboard/lib/filters'
import { decode } from '@aytm/app/insights/dashboard/lib/sanitize'
import { increaseLegibilityForLabel } from '@aytm/app/insights/dashboard/components/ui/elements/CustomDropdown.logic'

import Tooltip from '../Tooltip'

import './SubjectSelector.scss'

const CLASS = 'insights-portal-subject-selector'

const getSubjectText = (subject, split, showWaves) =>
  decode(
    subject.wave?.text && subject.split?.text && split && !showWaves
      ? `${subject.wave.text}: ${subject.split.text}`
      : subject.wave?.text
      ? subject.wave.text
      : subject.text || ''
  )

const SubjectSelector = props => {
  const subject = useMemo(() => props.subject || null, [props.subject])
  const subjects = useMemo(() => {
    const list = props.subjects || {}
    return props.showWaves
      ? Object.values(list).reduce((acc, cur) => {
          if ('add' === cur.uid) {
            acc.add = cur
          } else if (isBenchmark({ uid: cur.uid })) {
            acc[cur.uid] = cur
          } else if (cur.wave?.id && !acc[cur.wave.id]) {
            acc[cur.wave.id] = cur
          }
          return acc
        }, {})
      : list
  }, [props.showWaves, props.subjects])

  const inputRef = useRef()

  const text = useMemo(() => decode(subject?.text || props.placeholder || ''), [props.placeholder, subject?.text])

  const uid = useMemo(
    () => (props.showWaves ? subject?.wave?.id : subject?.uid) || null,
    [props.showWaves, subject?.uid, subject?.wave?.id]
  )

  const [placeholder, setPlaceholder] = useState(null)
  const [panelStyle, setPanelStyle] = useState(null)

  const options = useMemo(() => {
    const result = Object.values(subjects)
      .filter(v => v && v.uid !== uid && !isRowAveragesUid(v.uid))
      .map(v => {
        return { name: increaseLegibilityForLabel(getSubjectText(v, props.split, props.showWaves), 12), code: v.uid }
      })
    return props.sort ? sortOptions(result) : result
  }, [subjects, props.sort, props.split, props.showWaves, uid])

  const onChange = event => {
    if (props.onSelect) {
      if (!props.keepEmpty) setPlaceholder(event.value.name)
      props.onSelect({ uid: event.value.code })
    }
  }

  useEffect(() => {
    setPlaceholder(text)
  }, [text])

  useEffect(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setPanelStyle({ width: `${rect.width}px` })
    }
  }, [])

  const subjectTooltip =
    placeholder && placeholder.length > 50 ? <div className={`${CLASS}-tooltip`}>{decode(placeholder)}</div> : null

  return (
    <>
      <Dropdown
        confirmKeyboardSelect
        inputRef={inputRef}
        className={`insights-portal${props.className ? ` ${props.className}` : ''}`}
        panelClassName="insights-portal PRAYTMTheme"
        panelStyle={panelStyle}
        appendTo={document.body}
        value={null}
        options={options}
        optionLabel={'name'}
        placeholder={placeholder}
        filter
        dropdownIcon={props.icon || 'pi pi-chevron-down'}
        resetFilterOnHide={true}
        filterPlaceholder="Search items"
        disabled={props.disabled}
        onChange={onChange}
      />
      {props.tooltip && subjectTooltip ? (
        <Tooltip
          className={'test'}
          position={'up-right'}
          onTarget={() => inputRef.current}
          content={subjectTooltip}
          showOn={'mouseenter'}
          hideOn={'click&propagate mouseleave click:body wheel:body scroll:body'}
        />
      ) : null}
    </>
  )
}

SubjectSelector.propTypes = {
  className: PropTypes.string,
  subject: PropTypes.object,
  subjects: PropTypes.object,
  split: PropTypes.string,
  placeholder: PropTypes.any,
  index: PropTypes.number,
  keepEmpty: PropTypes.any,
  icon: PropTypes.string,
  tooltip: PropTypes.any,
  blink: PropTypes.any,
  sort: PropTypes.any,
  disabled: PropTypes.any,
  showWaves: PropTypes.any,
  onSelect: PropTypes.func
}

SubjectSelector.defaultProps = {
  blink: true
}

export default SubjectSelector
